import { useCallback } from "react"
import { DateTime } from "luxon"
import { download, generateCsv, mkConfig } from "export-to-csv"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { ProductItem, ProductTableRow } from "../productsTypes"
import { toDinero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"

type ExportCsvParams = Readonly<{
    data: ProductTableRow[] | undefined
}>

export function useExportProducts({ data }: ExportCsvParams) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({ locale: "da-DK" })
    const { l10n } = useAppLocalization()

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ",",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-products-${dateTimeFactory.formatDate(DateTime.now())}`,
    })

    const mapProductToCsvRow = (product: ProductItem, groupName: string) => {
        return {
            [l10n.getString("products-export-id")]: product.id !== undefined ? String(product.id) : "",
            [l10n.getString("products-export-product-number")]: product.productNumber ?? "",
            [l10n.getString("products-export-group")]: groupName,
            [l10n.getString("products-export-name")]: product.name ?? "",
            [l10n.getString("products-export-price")]: product.price
                ? moneyFactory.format(toDinero(product.price))
                : "",
            [l10n.getString("products-export-status")]:
                product.isActive !== undefined ? l10n.getString(product.isActive ? "active" : "inactive") : "",
            [l10n.getString("products-export-stock-total")]:
                product.stock?.inventory?.total !== undefined ? String(product.stock.inventory.total) : "",
            [l10n.getString("products-export-items-for-sale-per-day")]:
                product.stock?.sales?.daily?.itemsForSale !== undefined ? String(product.stock.sales.daily.itemsForSale) : "",
            [l10n.getString("products-export-items-left-today")]:
                product.stock?.sales?.daily?.remaining !== undefined ? String(product.stock.sales.daily.remaining) : "",
        }
    }

    return useCallback(() => {
        const emptyRow = () => ({
            [l10n.getString("products-export-id")]: "",
            [l10n.getString("products-export-product-number")]: "",
            [l10n.getString("products-export-group")]: "",
            [l10n.getString("products-export-name")]: "",
            [l10n.getString("products-export-price")]: "",
            [l10n.getString("products-export-status")]: "",
            [l10n.getString("products-export-stock-total")]: "",
            [l10n.getString("products-export-items-for-sale-per-day")]: "",
            [l10n.getString("products-export-items-left-today")]: "",
        })

        let exportRows: ReturnType<typeof mapProductToCsvRow>[]

        if (data && data.length > 0) {
            let currentGroupName = ""
            exportRows = []

            for (const row of data) {
                if (row.type === "group") {
                    currentGroupName = row.name
                } else {
                    exportRows.push(mapProductToCsvRow(row, currentGroupName))
                }
            }

            if (exportRows.length === 0) {
                exportRows = [emptyRow()]
            }
        } else {
            exportRows = [emptyRow()]
        }

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, moneyFactory, data, l10n])
}
